import { useQueryClient } from '@tanstack/react-query';

import {
  getApiV1SerieSerieidLangsQueryOptions,
  usePostApiV1SerieSerieidLangs,
  usePutApiV1SerieSerieidLangsId,
} from '@/shared/api/generated';
import type { SerieLangFormValues } from '../types/serieLangForm';

/** Callbacks do salvamento de Idioma da Série. */
export interface SaveSerieLangCallbacks {
  readonly onSuccess?: () => void;
  readonly onError?: () => void;
}

/**
 * Hook para criar/editar um Idioma da Série.
 *
 * Encapsula os mutations gerados pelo Kubb (`POST` e `PUT /api/v1/serie/{serieId}/langs[/{id}]`),
 * escolhendo a operação pela presença de `id` e invalidando o cache da listagem
 * no sucesso.
 *
 * @param serieId - ID da série
 *
 * @example
 * ```tsx
 * const { save, isPending } = useSaveSerieLang(serieId);
 * save(values, editingLangId, { onSuccess, onError });
 * ```
 */
export function useSaveSerieLang(serieId: number) {
  const queryClient = useQueryClient();
  const createMutation = usePostApiV1SerieSerieidLangs();
  const updateMutation = usePutApiV1SerieSerieidLangsId();

  const invalidate = () =>
    queryClient.invalidateQueries(getApiV1SerieSerieidLangsQueryOptions(serieId));

  const save = (
    values: SerieLangFormValues,
    id: number | undefined,
    callbacks?: SaveSerieLangCallbacks,
  ) => {
    const data = {
      cultureInfo: values.cultureInfo,
      title: values.title,
      resumee: values.description,
      header: values.header,
    };
    const onSuccess = () => {
      void invalidate();
      callbacks?.onSuccess?.();
    };
    const onError = () => callbacks?.onError?.();

    if (id === undefined) {
      createMutation.mutate({ serieId, data }, { onSuccess, onError });
    } else {
      updateMutation.mutate({ serieId, id, data }, { onSuccess, onError });
    }
  };

  return {
    save,
    isPending: createMutation.isPending || updateMutation.isPending,
  } as const;
}
